import React, { useState, useEffect } from 'react';
import { getBackendURL } from '../../utils/backendURL';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  Save, 
  X, 
  Edit, 
  DollarSign, 
  Users, 
  Zap,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Crown,
  CreditCard,
  FolderKanban,
  Plus
} from 'lucide-react';

const BACKEND_URL = getBackendURL();

const PlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlanForm, setNewPlanForm] = useState({
    plan_code: '',
    name: '',
    display_name: '',
    price_usd: 0,
    executions_monthly: 50,
    team_members_included: 1,
    max_projects: 1,
    overage_enabled: false,
    overage_price_usd: null,
    description: '',
    features: '',
    limitations: '',
    highlight_badge: '',
    popular: false,
    trial_days: '',
    stripe_price_id: '',
    is_active: true
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/plans/`,
        { headers: getAuthHeaders() }
      );
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      showMessage('Failed to load plans', 'error');
    } finally {
      setLoading(false);
    }
  };

  const createPlan = async () => {
    try {
      setSaving(true);
      
      const planData = {
        plan_code: newPlanForm.plan_code.toUpperCase().replace(/\s+/g, '_'),
        name: newPlanForm.name,
        display_name: newPlanForm.display_name || null,
        price_usd: parseFloat(newPlanForm.price_usd) || 0,
        executions_monthly: parseInt(newPlanForm.executions_monthly) || 50,
        team_members_included: parseInt(newPlanForm.team_members_included) || 1,
        max_projects: parseInt(newPlanForm.max_projects) || 1,
        overage_enabled: newPlanForm.overage_enabled,
        overage_price_usd: newPlanForm.overage_price_usd ? parseFloat(newPlanForm.overage_price_usd) : null,
        description: newPlanForm.description,
        features: newPlanForm.features.split('\n').filter(f => f.trim()),
        limitations: newPlanForm.limitations ? newPlanForm.limitations.split('\n').filter(l => l.trim()) : [],
        highlight_badge: newPlanForm.highlight_badge || null,
        popular: newPlanForm.popular,
        trial_days: newPlanForm.trial_days ? parseInt(newPlanForm.trial_days) : null,
        stripe_price_id: newPlanForm.stripe_price_id || null,
        is_active: newPlanForm.is_active
      };

      await axios.post(
        `${BACKEND_URL}/api/admin/plans/create`,
        planData,
        { headers: getAuthHeaders() }
      );

      showMessage('Plan created successfully!', 'success');
      setShowCreateModal(false);
      setNewPlanForm({
        plan_code: '',
        name: '',
        display_name: '',
        price_usd: 0,
        executions_monthly: 50,
        team_members_included: 1,
        max_projects: 1,
        overage_enabled: false,
        overage_price_usd: null,
        description: '',
        features: '',
        limitations: '',
        highlight_badge: '',
        popular: false,
        trial_days: '',
        stripe_price_id: '',
        is_active: true
      });
      await fetchPlans();
    } catch (error) {
      console.error('Failed to create plan:', error);
      showMessage(error.response?.data?.detail || 'Failed to create plan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (plan) => {
    setEditingPlan(plan.plan_code);
    setEditForm({
      name: plan.name,
      display_name: plan.display_name || '',
      price_usd: plan.price_usd,
      executions_monthly: plan.executions_monthly,
      team_members_included: plan.team_members_included,
      max_projects: plan.max_projects ?? 1,
      overage_enabled: plan.overage_enabled,
      overage_price_usd: plan.overage_price_usd,
      description: plan.description,
      features: plan.features.join('\n'),
      limitations: plan.limitations.join('\n'),
      highlight_badge: plan.highlight_badge || '',
      popular: plan.popular,
      trial_days: plan.trial_days || '',
      stripe_price_id: plan.stripe_price_id || '',
      is_active: plan.is_active
    });
  };

  const cancelEditing = () => {
    setEditingPlan(null);
    setEditForm({});
  };

  const savePlan = async (planCode) => {
    try {
      setSaving(true);
      
      const updateData = {
        name: editForm.name,
        display_name: editForm.display_name || null,
        price_usd: parseFloat(editForm.price_usd),
        executions_monthly: parseInt(editForm.executions_monthly),
        team_members_included: parseInt(editForm.team_members_included),
        max_projects: parseInt(editForm.max_projects),
        overage_enabled: editForm.overage_enabled,
        overage_price_usd: editForm.overage_price_usd ? parseFloat(editForm.overage_price_usd) : null,
        description: editForm.description,
        features: editForm.features.split('\n').filter(f => f.trim()),
        limitations: editForm.limitations ? editForm.limitations.split('\n').filter(l => l.trim()) : [],
        highlight_badge: editForm.highlight_badge || null,
        popular: editForm.popular,
        trial_days: editForm.trial_days ? parseInt(editForm.trial_days) : null,
        stripe_price_id: editForm.stripe_price_id || null,
        is_active: editForm.is_active
      };

      await axios.put(
        `${BACKEND_URL}/api/admin/plans/${planCode}`,
        updateData,
        { headers: getAuthHeaders() }
      );

      showMessage('Plan updated successfully!', 'success');
      await fetchPlans();
      cancelEditing();
    } catch (error) {
      console.error('Failed to update plan:', error);
      showMessage('Failed to update plan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const initializePlans = async () => {
    try {
      setLoading(true);
      await axios.post(
        `${BACKEND_URL}/api/admin/plans/initialize`,
        {},
        { headers: getAuthHeaders() }
      );
      showMessage('Plans initialized successfully!', 'success');
      await fetchPlans();
    } catch (error) {
      console.error('Failed to initialize plans:', error);
      showMessage(error.response?.data?.detail || 'Failed to initialize plans', 'error');
    } finally {
      setLoading(false);
    }
  };

  const syncSubscriptions = async () => {
    if (!window.confirm('This will update all existing user subscriptions to match the current plan configurations. Continue?')) {
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/plans/sync-subscriptions`,
        {},
        { headers: getAuthHeaders() }
      );
      showMessage(
        `Subscriptions synced! Updated: ${response.data.updated}, Skipped: ${response.data.skipped}`, 
        'success'
      );
    } catch (error) {
      console.error('Failed to sync subscriptions:', error);
      showMessage(error.response?.data?.detail || 'Failed to sync subscriptions', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  if (loading && plans.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
        <span className="ml-3 text-lg">Loading plans...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Plan Management</h2>
          <p className="text-gray-600 mt-1">Manage subscription plans, pricing, and features</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowCreateModal(true)} 
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Crown className="h-4 w-4 mr-2" />
            Create Plan
          </Button>
          {plans.length === 0 ? (
            <Button onClick={initializePlans} className="bg-purple-600 hover:bg-purple-700">
              <Crown className="h-4 w-4 mr-2" />
              Initialize Plans
            </Button>
          ) : (
            <Button 
              onClick={syncSubscriptions} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync All Users
            </Button>
          )}
        </div>
      </div>

      {/* Create Plan Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 m-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Create New Plan</h3>
              <Button size="sm" variant="outline" onClick={() => setShowCreateModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Plan Code *</label>
                  <Input
                    value={newPlanForm.plan_code}
                    onChange={(e) => setNewPlanForm({...newPlanForm, plan_code: e.target.value})}
                    placeholder="e.g., BUSINESS_TRIAL"
                  />
                  <p className="text-xs text-gray-500 mt-1">Unique identifier (auto-uppercased)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Plan Name *</label>
                  <Input
                    value={newPlanForm.name}
                    onChange={(e) => setNewPlanForm({...newPlanForm, name: e.target.value})}
                    placeholder="e.g., Business Trial"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Display Name (Badge)</label>
                <Input
                  value={newPlanForm.display_name}
                  onChange={(e) => setNewPlanForm({...newPlanForm, display_name: e.target.value})}
                  placeholder="Custom name shown in user dashboard badge (optional)"
                />
                <p className="text-xs text-gray-500 mt-1">If empty, Plan Name will be used in the badge</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Price (USD)</label>
                  <Input
                    type="number"
                    value={newPlanForm.price_usd}
                    onChange={(e) => setNewPlanForm({...newPlanForm, price_usd: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Executions/Month</label>
                  <Input
                    type="number"
                    value={newPlanForm.executions_monthly}
                    onChange={(e) => setNewPlanForm({...newPlanForm, executions_monthly: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Trial Days</label>
                  <Input
                    type="number"
                    value={newPlanForm.trial_days}
                    onChange={(e) => setNewPlanForm({...newPlanForm, trial_days: e.target.value})}
                    placeholder="e.g., 14"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Team Members (-1 = unlimited)</label>
                  <Input
                    type="number"
                    value={newPlanForm.team_members_included}
                    onChange={(e) => setNewPlanForm({...newPlanForm, team_members_included: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Projects (-1 = unlimited)</label>
                  <Input
                    type="number"
                    value={newPlanForm.max_projects}
                    onChange={(e) => setNewPlanForm({...newPlanForm, max_projects: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Input
                  value={newPlanForm.description}
                  onChange={(e) => setNewPlanForm({...newPlanForm, description: e.target.value})}
                  placeholder="Plan description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Features (one per line)</label>
                <Textarea
                  value={newPlanForm.features}
                  onChange={(e) => setNewPlanForm({...newPlanForm, features: e.target.value})}
                  rows={4}
                  placeholder="Flow Testing&#10;API Testing&#10;50 executions/month"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Highlight Badge</label>
                  <Input
                    value={newPlanForm.highlight_badge}
                    onChange={(e) => setNewPlanForm({...newPlanForm, highlight_badge: e.target.value})}
                    placeholder="e.g., Trial, Most Popular"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Overage Price (USD)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newPlanForm.overage_price_usd || ''}
                    onChange={(e) => setNewPlanForm({...newPlanForm, overage_price_usd: e.target.value})}
                    disabled={!newPlanForm.overage_enabled}
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newPlanForm.overage_enabled}
                    onChange={(e) => setNewPlanForm({...newPlanForm, overage_enabled: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Overage Enabled</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newPlanForm.popular}
                    onChange={(e) => setNewPlanForm({...newPlanForm, popular: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Popular Badge</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newPlanForm.is_active}
                    onChange={(e) => setNewPlanForm({...newPlanForm, is_active: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={createPlan}
                  disabled={saving || !newPlanForm.plan_code || !newPlanForm.name}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {saving ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Create Plan
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            )}
            <span className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {message.text}
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {plans.map((plan) => (
          <Card key={plan.plan_code} className="p-6">
            {editingPlan === plan.plan_code ? (
              // Edit Mode
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">Edit {plan.name}</h3>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={cancelEditing}
                      disabled={saving}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => savePlan(plan.plan_code)}
                      disabled={saving}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {saving ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-1" />
                      )}
                      Save
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Plan Name</label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Display Name (Badge)</label>
                  <Input
                    value={editForm.display_name}
                    onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                    placeholder="Custom name shown in user dashboard badge (optional)"
                  />
                  <p className="text-xs text-gray-500 mt-1">If empty, Plan Name will be used in the badge</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Price (USD)</label>
                    <Input
                      type="number"
                      value={editForm.price_usd}
                      onChange={(e) => setEditForm({...editForm, price_usd: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Executions/Month</label>
                    <Input
                      type="number"
                      value={editForm.executions_monthly}
                      onChange={(e) => setEditForm({...editForm, executions_monthly: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Team Members (-1 = unlimited)</label>
                    <Input
                      type="number"
                      value={editForm.team_members_included}
                      onChange={(e) => setEditForm({...editForm, team_members_included: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Projects (-1 = unlimited)</label>
                    <Input
                      type="number"
                      value={editForm.max_projects}
                      onChange={(e) => setEditForm({...editForm, max_projects: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Overage Price (USD)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editForm.overage_price_usd || ''}
                      onChange={(e) => setEditForm({...editForm, overage_price_usd: e.target.value})}
                      disabled={!editForm.overage_enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Trial Days</label>
                    <Input
                      type="number"
                      value={editForm.trial_days}
                      onChange={(e) => setEditForm({...editForm, trial_days: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Input
                    value={editForm.description}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Features (one per line)</label>
                  <Textarea
                    value={editForm.features}
                    onChange={(e) => setEditForm({...editForm, features: e.target.value})}
                    rows={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Limitations (one per line)</label>
                  <Textarea
                    value={editForm.limitations}
                    onChange={(e) => setEditForm({...editForm, limitations: e.target.value})}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Highlight Badge</label>
                    <Input
                      value={editForm.highlight_badge}
                      onChange={(e) => setEditForm({...editForm, highlight_badge: e.target.value})}
                      placeholder="e.g., Most Popular"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Stripe Price ID</label>
                  <Input
                    value={editForm.stripe_price_id}
                    onChange={(e) => setEditForm({...editForm, stripe_price_id: e.target.value})}
                    placeholder="e.g., price_1234567890abcdefg"
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the Stripe Price ID for this plan (found in Stripe Dashboard)
                  </p>
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.overage_enabled}
                      onChange={(e) => setEditForm({...editForm, overage_enabled: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Overage Enabled</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.popular}
                      onChange={(e) => setEditForm({...editForm, popular: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Popular Badge</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                </div>
              </div>
            ) : (
              // View Mode
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold">{plan.name}</h3>
                    {plan.highlight_badge && (
                      <Badge className="bg-purple-100 text-purple-700">{plan.highlight_badge}</Badge>
                    )}
                    {plan.popular && (
                      <Badge className="bg-yellow-100 text-yellow-700">Popular</Badge>
                    )}
                    {!plan.is_active && (
                      <Badge variant="outline" className="text-gray-500">Inactive</Badge>
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => startEditing(plan)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </div>

                <p className="text-gray-600 text-sm mb-4">{plan.description}</p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-gray-600">Price</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">${plan.price_usd}</p>
                    <p className="text-xs text-gray-500">per month</p>
                  </div>

                  <div className="bg-purple-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="h-4 w-4 text-purple-600" />
                      <span className="text-sm text-gray-600">Executions</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">{plan.executions_monthly.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">per month</p>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-gray-500" />
                    <span>
                      {plan.team_members_included === -1 ? 'Unlimited' : plan.team_members_included} Team Members
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FolderKanban className="h-4 w-4 text-gray-500" />
                    <span>
                      {plan.max_projects === -1 ? 'Unlimited' : (plan.max_projects ?? 1)} Projects
                    </span>
                  </div>
                  {plan.overage_enabled && (
                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="h-4 w-4 text-gray-500" />
                      <span>
                        Overage: ${plan.overage_price_usd || 'Custom'} per execution
                      </span>
                    </div>
                  )}
                  {plan.trial_days && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-gray-500" />
                      <span>{plan.trial_days}-day trial</span>
                    </div>
                  )}
                  {plan.stripe_price_id && (
                    <div className="flex items-start gap-2 text-sm">
                      <CreditCard className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-600 font-medium">Stripe Price ID:</div>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-800 break-all">
                          {plan.stripe_price_id}
                        </code>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Features ({plan.features.length})</p>
                  <ul className="text-sm space-y-1 max-h-32 overflow-y-auto">
                    {plan.features.slice(0, 5).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                    {plan.features.length > 5 && (
                      <li className="text-xs text-gray-500 ml-6">
                        +{plan.features.length - 5} more features
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PlanManagement;
